/**
 * chatSessionCache — module-level cache of Fias AI chat sessions keyed by
 * an arche-supplied sessionKey.
 *
 * Why this exists: each arche page mounts its own FiasAIProvider, so the
 * chat state lives inside that provider and is destroyed when the user
 * navigates away. This cache persists the messages + conversationId across
 * unmount/remount cycles for the lifetime of the browser tab, so the user
 * can close the AI panel, click around, come back, and still see the thread
 * they were working on.
 *
 * Scope:
 *  - Lives in module memory, not sessionStorage, so it clears on page
 *    reload. That's intentional: the backend conversation is keyed to the
 *    conversationId, which we also cache, and we don't want stale local
 *    threads hanging around indefinitely.
 *  - Keyed by a caller-supplied string. The travel arche uses
 *    `arche_travel:<tripId>` so each trip gets its own thread, and
 *    `arche_travel:list` for the list page.
 *  - Subscribers are notified when a key's session changes so hydrated
 *    hooks can stay in sync if state is updated from another mount.
 */

import type { FiasAIChatMessage } from './useFiasAIChat';

export interface ChatSessionSnapshot {
  messages: FiasAIChatMessage[];
  conversationId: string | null;
}

const EMPTY: ChatSessionSnapshot = { messages: [], conversationId: null };

const store = new Map<string, ChatSessionSnapshot>();

export function getCachedSession(key: string): ChatSessionSnapshot {
  return store.get(key) ?? EMPTY;
}

export function setCachedSession(key: string, snapshot: ChatSessionSnapshot): void {
  store.set(key, snapshot);
}

export function clearCachedSession(key: string): void {
  store.delete(key);
}
